import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, inject, Inject, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import {
  SEAL_VERSION,
  SealConfig,
  SealConfigService,
  SealMediaWatcherService,
  SealNavigationService,
  SealPlatformService,
  SealVerticalNavigationComponent
} from '@shipsfocus/seal';
import { ClassicLayoutComponent } from 'app/layout/layouts/classic/classic.component';
import { EmptyLayoutComponent } from 'app/layout/layouts/empty/empty.component';
import { ModernLayoutComponent } from 'app/layout/layouts/modern/modern.component';
import { combineLatest, filter, map, Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [EmptyLayoutComponent, ModernLayoutComponent, ClassicLayoutComponent]
})
export class LayoutComponent implements OnInit, OnDestroy {
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  config: SealConfig;
  layout: string;
  scheme: 'dark' | 'light';
  theme: string;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    @Inject(DOCUMENT) private _document: any,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _renderer2: Renderer2,
    private _sealConfigService: SealConfigService,
    private _sealNavigationService: SealNavigationService,
    private _sealMediaWatcherService: SealMediaWatcherService,
    private _sealPlatformService: SealPlatformService,
    private _translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    combineLatest([
      this._sealConfigService.config$,
      this._sealMediaWatcherService.onMediaQueryChange$(['(prefers-color-scheme: dark)', '(prefers-color-scheme: light)'])
    ])
      .pipe(
        map(([config, mql]: any) => {
          const options = {
            scheme: config.scheme,
            theme: config.theme
          };

          this.config = config;

          if (config.scheme === 'auto') {
            options.scheme = mql.breakpoints['(prefers-color-scheme: dark)'] ? 'dark' : 'light';
          }

          return options;
        }),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((options) => {
        this.scheme = options.scheme;
        this.theme = options.theme;

        this._updateLayout();
        this._updateScheme();
        this._updateTheme();

        setTimeout(() => this._updateNavigation(), 300);
      });

    this._translocoService.langChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this._updateNavigation();
    });

    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        this._updateLayout();
      });

    this._renderer2.setAttribute(this._document.querySelector('[ng-version]'), 'seal-version', SEAL_VERSION);

    this._renderer2.addClass(this._document.body, this._sealPlatformService.osName);
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  private _updateLayout(): void {
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    this.layout = this.config.layout;

    const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
    if (layoutFromQueryParam) {
      this.layout = layoutFromQueryParam;
      if (this.config) {
        this.config.layout = layoutFromQueryParam;
      }
    }

    const paths = route.pathFromRoot;
    paths.forEach((path) => {
      if (path.routeConfig && path.routeConfig.data && path.routeConfig.data.layout) {
        this.layout = path.routeConfig.data.layout;
      }
    });
  }

  private _updateScheme(): void {
    this._document.body.classList.remove('light', 'dark');

    this._document.body.classList.add(this.scheme);
  }

  private _updateTheme(): void {
    this._document.body.classList.forEach((className: string) => {
      if (className.startsWith('theme-')) {
        this._document.body.classList.remove(className, className.split('-')[1]);
      }
    });

    this._document.body.classList.add(this.theme);
  }

  private _updateNavigation(): void {
    const navComponent = this._sealNavigationService.getComponent<SealVerticalNavigationComponent>('mainNavigation');

    if (!navComponent) {
      return null;
    }

    const navigation = navComponent.navigation;

    const navIds = navigation.flatMap((nav) => [nav.id, ...(nav.children ? nav.children.map((child) => child.id) : [])]);

    navIds.forEach((id) => {
      const navigationItem = this._sealNavigationService.getItem(id, navigation);

      if (navigationItem) {
        this._translocoService
          .selectTranslateObject(`nav.${id.replace(/_/g, '-')}`)
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe((translation) => {
            navigationItem.title = translation.title || translation;

            if (translation?.subtitle && this.config?.layout !== 'modern') {
              navigationItem.subtitle = translation.subtitle || '';
            }
            navComponent.refresh();
          });
      }
    });
  }
}
